import { createContext } from '@shared/utils/createContext';
import { noop } from '@shared/utils/noop';
import { CARD_DELAY_FIRST_MS } from './GetStartedOnboarding.constants';
import type { GetStartedTourContextValue, GetStartedTourState } from './GetStartedOnboarding.types';

export const INACTIVE_TOUR_STATE: GetStartedTourState = {
  tourOpen: false,
  step: 0,
  drawerOpen: false,
  awaiting: null,
  awaitingRingDismissed: false,
  celebrate: false,
  done: [],
  phase: 'in',
  cardDelayMs: CARD_DELAY_FIRST_MS,
  dismissedThisSession: false,
  heroMenuOpenSignal: 0,
  templatesStage: 'trigger',
  invitePartnerSignal: 0,
};

/**
 * Inactive default so consumers (TopBar button, target registrations, completion hooks) are safe
 * no-ops anywhere the provider isn't mounted — tests, storybook, non-admin shells.
 */
const INACTIVE_CONTEXT_VALUE: GetStartedTourContextValue = {
  isEnabled: false,
  isTourActive: false,
  // No provider mounted means no tour anywhere in this tree (guest shells, tests, storybook) —
  // that is a settled answer, not an unknown one.
  isTourPending: false,
  state: INACTIVE_TOUR_STATE,
  visibleTaskId: null,
  remainingCount: 0,
  tryIt: noop,
  dismissAwaitingRing: noop,
  completeTask: noop,
  creditTask: noop,
  celebrateTask: noop,
  setTemplatesStage: noop,
  acknowledgeTemplatesReady: noop,
  closeCoachmark: noop,
  dismissTour: noop,
  restartTour: noop,
  closeDrawer: noop,
  toggleDrawer: noop,
  jumpToTask: noop,
  registerTarget: noop,
  getTarget: () => null,
  setCoachmarkSuppressed: noop,
  onLearnMore: noop,
};

/**
 * `strict: false` with an `initialValue`, so a consumer outside the provider gets the inert value
 * above rather than a thrown error — the whole point of the inactive default. Same shape as
 * `Suspense.tsx` and `GuestDisplayLocaleContext.tsx`.
 */
const [GetStartedTourContextProvider, useGetStartedTour, GetStartedTourContext] = createContext<GetStartedTourContextValue>({
  name: 'GetStartedTourContext',
  strict: false,
  initialValue: INACTIVE_CONTEXT_VALUE,
});

export { GetStartedTourContext, GetStartedTourContextProvider, useGetStartedTour };
